<template>
    <div>
        <!--User Details Collapse-->
        <user-details-form
            :theme="theme"
            :box-class="boxClass"
            :enabled="enabled"
            :user="user"
            :occasion-additions="occasion.occasion_additions"
            :additional-transaction-values="additionalTransactionValues"
        ></user-details-form>

        <!--Pre-paid box - Select Tickets Panel-->
        <div class="mb-4" :class="{'tickets-section-disabled': !userDetailsValid}">
            <h3 class="select-tickets-heading">
                <span class="step-badge mr-2">{{ stepTickets }}</span>
                <i class="fas fa-ticket-alt mr-2"></i>{{ $t('bulk_form.select_tickets') }}
            </h3>

            <div class="d-none d-sm-block">
                <!-- Message when user details not completed - Desktop -->
                <div v-if="!userDetailsValid" class="alert alert-info mb-3" role="alert">
                    <i class="fas fa-info-circle mr-2"></i>
                    <strong>Please complete your details above before selecting tickets.</strong>
                </div>
                <div class="tickets-container">
                    <ticket-list
                        :groups="groups"
                        mode="bulk"
                        default-view="table"
                        :show-view-selector="false"
                        v-model="tickets"
                        :disabled="!enabled"
                        :top-up-bulk-action="topUpBulkAction"
                    ></ticket-list>
                </div>

                <!-- Totals Summary Panel - Desktop -->
                <div class="totals-summary-panel mt-3 d-none d-sm-block">
                    <div class="summary-item">
                        <span class="summary-label">{{ $t('bulk_form.total_tickets') }}</span>
                        <span class="summary-value">{{ totalNumOfTickets }}</span>
                    </div>
                    <div v-if="bundleBooking !== null && !bundleBooking.prevent_prepay_discount" class="mt-3 pt-3 border-top">
                        <display-bundle-booking-benefits
                            :bundle-booking="bundleBooking"
                            :number-of-participants-selected="totalNumOfTickets"
                        ></display-bundle-booking-benefits>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile View - Select Tickets Panel -->
        <div v-if="theme == 'bs4'" class="d-block d-sm-none mb-4" :class="{'tickets-section-disabled': !userDetailsValid}">
            <!-- Message when user details not completed - Mobile -->
            <div v-if="!userDetailsValid" class="alert alert-info mb-3" role="alert">
                <i class="fas fa-info-circle mr-2"></i>
                <strong>Please complete your details above before selecting tickets.</strong>
            </div>

            <div class="mobile-tickets-container">
                <div v-for="(group, groupIndex) in groups" :key="group.title || groupIndex" :class="{'mt-4': groupIndex > 0}">
                    <h4 v-if="group.title" class="mobile-group-title">
                        {{ group.title }}
                    </h4>
                    <div class="mobile-card-container">
                        <div class="mobile-ticket-card"
                             v-for="registrationCategory in group.registration_categories"
                             :key="registrationCategory.id"
                             :class="{'mobile-ticket-card-selected': tickets[registrationCategory.id] > 0}">
                            <div class="mobile-card-content">
                                <h6 class="mobile-card-title">{{ registrationCategory.name }}</h6>
                                <p class="mobile-card-date">
                                    {{
                                        registrationCategory.date | dateFormat(occasion.timezone,
                                            "DD/MM/YY HH:mm")
                                    }}
                                </p>
                                <p class="mobile-card-price">
                                    {{
                                        registrationCategory.current_price.amount |
                                            money(registrationCategory.current_price.currency)
                                    }}
                                </p>
                                <div class="mb-3">
                                    <status-badge :status="registrationCategory.human_status"></status-badge>
                                </div>
                            </div>
                            <div class="mobile-card-quantity">
                                <span class="mobile-quantity-label">Quantity:</span>
                                <plus-minus-control
                                    style="display:inline-block"
                                    v-model="tickets[registrationCategory.id]"
                                    :disabled="!enabled"
                                    :max="ParseInt(registrationCategory.pre_pay_setting.data.max_pre_pay_quantity)"
                                    :min="ParseInt(registrationCategory.pre_pay_setting.data.min_pre_pay_quantity)"
                                ></plus-minus-control>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Totals Summary Panel - Mobile -->
            <div class="totals-summary-panel-mobile mt-3 d-block d-sm-none">
                <div class="summary-item">
                    <span class="summary-label">{{ $t('bulk_form.total_tickets') }}</span>
                    <span class="summary-value">{{ totalNumOfTickets }}</span>
                </div>
                <div v-if="bundleBooking !== null && !bundleBooking.prevent_prepay_discount" class="mt-3 pt-3 border-top">
                    <display-bundle-booking-benefits
                        :bundle-booking="bundleBooking"
                        :number-of-participants-selected="totalNumOfTickets"
                    ></display-bundle-booking-benefits>
                </div>
            </div>
        </div>


        <!--Select Team - Shared across occasion/global-->
        <div v-if="selectedGroupCategoriesWithSharedTeams.length > 0" class="mb-4">
            <h3 class="select-tickets-heading">
                <span class="step-badge mr-2">{{ stepTeam }}</span>
                <i class="fas fa-users mr-2"></i>{{ $t('bulk_form.setup_team_shared') }}
            </h3>
            <div class="team-container text-center">
                <p>{{
                        'team_picker_description' | mapField(occasion.field_mappings, 'If you want to setup a team, please select below.')
                    }}</p>
                <div class="shared-team-races mb-3">
                    <p class="mb-2"><strong>This team will be used for:</strong></p>
                    <ul class="text-left">
                        <li v-for="rc in selectedGroupCategoriesWithSharedTeams" :key="rc.id">
                            {{ rc.name }}
                        </li>
                    </ul>
                </div>
                <team-picker-form
                    :field-mappings="occasion.field_mappings"
                    :opt-out-options="selectedGroupCategoriesWithSharedTeams[0].opt_out_occasion_form_fields.filter(oooff => oooff.name.slice(0, 18).toLowerCase() == 'team_picker_option').map(oooff => oooff.name.slice(19, oooff.name.length))"
                    :registration-category-ids="selectedGroupCategoriesWithSharedTeams.map(rc => rc.id)"
                    :show-validation-errors="showValidationErrors"
                    v-model="sharedTeamId"
                    :team="selectedGroupCategoriesWithSharedTeams[0].team"
                    :team-id-prop="selectedGroupCategoriesWithSharedTeams[0].hasOwnProperty('team') && Boolean(selectedGroupCategoriesWithSharedTeams[0].team) ? selectedGroupCategoriesWithSharedTeams[0].team.id : null"
                    @validation="setValidated($event)"
                ></team-picker-form>
            </div>
        </div>

        <!--Select Team - Separate per registration category-->
        <div>
            <div
                class="mb-4"
                v-for="registrationCategory in selectedGroupCategoriesWithSeparateTeams"
                :key="registrationCategory.id">

                <h3 class="select-tickets-heading">
                    <span class="step-badge mr-2">{{ stepTeam }}</span>
                    <i class="fas fa-users mr-2"></i>{{ $t('bulk_form.setup_team', { name: registrationCategory.name }) }}
                </h3>

                <div class="team-container text-center">
                    <p>{{
                            'team_picker_description' | mapField(occasion.field_mappings, 'If you want to setup a team, please select below.')
                        }}</p>
                    <team-picker-form
                        :field-mappings="occasion.field_mappings"
                        :opt-out-options="registrationCategory.opt_out_occasion_form_fields.filter(oooff => oooff.name.slice(0, 18).toLowerCase() == 'team_picker_option').map(oooff => oooff.name.slice(19, oooff.name.length))"
                        :registration-category-id="registrationCategory.id"
                        :show-validation-errors="showValidationErrors"
                        v-model="separateTeamIds[registrationCategory.id]"
                        :team="registrationCategory.team"
                        :team-id-prop="registrationCategory.hasOwnProperty('team') && Boolean(registrationCategory.team) ? registrationCategory.team.id : null"
                        @validation="setValidated($event)"
                    ></team-picker-form>
                </div>
            </div>
        </div>

        <!--Donations-->
        <div v-if="charities.length" class="mb-4">
            <h3 class="select-tickets-heading">
                <span class="step-badge mr-2">{{ stepDonations }}</span>
                <i class="fas fa-heart mr-2"></i>{{ $t('bulk_form.donate') }}
            </h3>
            <donation-group
                :charities="charities"
                :single-address="true"
                :user-entity="upToDateUserEntity"
                :submittable="false"
                :theme="theme"
                v-model="donations"
                :disabled="!enabled"
            ></donation-group>
        </div>

        <!--Submit button-->
        <b-button variant="success" :disabled="!userDetailsValid || !totalNumOfTickets || submitting" @click="submit"
                  :block="true">
            <span v-if="!submitting">{{ $t('bulk_form.continue') }}</span>
            <span v-else>{{ $t('bulk_form.submitting') }}</span>
        </b-button>
    </div>
</template>

<script>
import DonationGroup from "../../Donations/Form/Bulk.vue";
import StatusBadge from '../StatusBadge.vue'
import PlusMinusControl from '../../AAA_TO_REFACTOR/PlusMinsControl.vue'
import {dateFormat} from '../../Utility/filters/dates'
import {money} from '../../Utility/filters/money'
import {pick} from "lodash-es"
import axios from 'axios'
import TeamPickerForm from "../../Teams/Form/Picker";
import BFormInput from 'bootstrap-vue/es/components/form-input/form-input';
import {mapField} from "../../FieldMappings/filters/mapField";
import DisplayBundleBookingBenefits from "../../BundleBookings/DisplayBenefits.vue";
import ParseInt from "lodash-es/parseInt";
import UserDetailsForm from "./UserDetailsForm.vue";
import TicketList from "../../RegistrationCategories/Tickets/List.vue";
import boxClassMixin from "./mixins/boxClassMixin.js";
import formValidationMixin from "./mixins/formValidationMixin.js";

export default {
    name: "BulkParticipant",
    mixins: [boxClassMixin, formValidationMixin],
    components: {
        BFormInput,
        DisplayBundleBookingBenefits,
        DonationGroup,
        PlusMinusControl,
        StatusBadge,
        TeamPickerForm,
        TicketList,
        UserDetailsForm,
    },
    filters: {
        money,
        dateFormat,
        mapField,
    },
    props: {
        occasion: {
            type: Object,
            required: true,
        },
        userEntity: { // TODO: extremely insecure, change ASAP (Ben did not write this...)
            type: [Object, Boolean],
            required: false,
        },
        theme: {
            type: String,
            default: 'bs4',
        },
        transaction: {
            type: Object,
            required: false,
            default: () => ({})
        },
        topUpBulkAction: {
            type: Boolean,
            required: false,
            default: false,
        }
    },
    data() {
        return {
            additionalTransactionValues: {},
            donations: this.transaction.donations, //TODO -  Map any donations from the transaction
            fields: ['name', 'date', 'price', 'ticket'],
            tickets: {},
            sharedTeamId: null,
            separateTeamIds: {},
            user: {
                id: '',
                first_name: '',
                last_name: '',
                confirmed: false
            },
        }
    },

    created() {
        this.registrationCategories.forEach(({id}) => {
            this.$set(this.tickets, id, 0);
        });

        if (this.userEntity) {
            this.user = this.userEntity;
            this.user.confirmed = true;
        }
        //Pre-select any already selected tickets (editing)
        if (this.transaction.hasOwnProperty('participants') && this.transaction.participants.length > 0) {
            this.transaction.participants.map(p => p.registration_category_id).forEach((x) => {
                if (this.registrationCategories.find(registrationCategory => registrationCategory.id == x)) {
                    this.tickets[x] = (this.tickets[x] || 0) + 1;
                }
            });
        }
        //Generate any additional transaction values
        this.transaction.hasOwnProperty('additional_transaction_values') && this.transaction.additional_transaction_values.forEach((v, k) => {
            this.additionalTransactionValues[v.addition_id] = v.value;
        });
    },
    computed: {
        groups() {
            let groups = this.occasion.groups || [];
            return groups.sort((a, b) => a.title.localeCompare(b.title));
        },
        bundleBooking() {
            if (Object.prototype.hasOwnProperty.call(this.occasion, "bundle_bookings") && this.occasion.bundle_bookings.length > 0) {
                return this.occasion.bundle_bookings[0];
            } // true

            return null;
        },
        upToDateUserEntity() {
            return Object.assign({}, this.userEntity, this.user);
        },
        registrationCategories() {
            return this.occasion.registration_categories
                .map(registrationCategory => {
                    let
                        computedRegistrationCategory = pick(registrationCategory, [
                            'id',
                            'name',
                            'date',
                            'timezone',
                            'current_price.amount',
                            'current_price.currency',
                            'human_status',
                            'max_pre_pay_quantity',
                            'min_pre_pay_quantity',
                            'allowed_team_entries',
                            'opt_out_occasion_form_fields',
                            'pre_pay_setting',
                            'team_sharing_strategy',
                        ]),
                        participant = this.transaction.hasOwnProperty('participants') && this.transaction.participants.length
                            ? this.transaction.participants.find(participant => participant.registration_category_id == registrationCategory.id && participant.team_id)
                            : null
                    ;

                    computedRegistrationCategory.team = participant
                        ? participant.team
                        : null;

                    return computedRegistrationCategory;
                });
        },
        selectedGroupCategories() {
            return this.registrationCategories.filter(registrationCategory =>
                this.tickets[registrationCategory.id] > 0 && registrationCategory.allowed_team_entries
            );
        },
        selectedGroupCategoriesWithSharedTeams() {
            return this.selectedGroupCategories.filter(registrationCategory =>
                registrationCategory.team_sharing_strategy === 'occasion' ||
                registrationCategory.team_sharing_strategy === 'global'
            );
        },
        selectedGroupCategoriesWithSeparateTeams() {
            return this.selectedGroupCategories.filter(registrationCategory =>
                registrationCategory.team_sharing_strategy === 'registration_category'
            );
        },
        teams() {
            return Object.fromEntries(this.registrationCategories
                .map(registrationCategory => {
                    // For group categories with tickets selected
                    if (this.tickets[registrationCategory.id] > 0 && registrationCategory.allowed_team_entries) {
                        // Use shared team ID for occasion/global sharing strategies
                        if (registrationCategory.team_sharing_strategy === 'occasion' ||
                            registrationCategory.team_sharing_strategy === 'global') {
                            return [registrationCategory.id, this.sharedTeamId || (registrationCategory.team ? registrationCategory.team.id : null)];
                        }
                        // Use separate team ID for registration_category strategy
                        if (registrationCategory.team_sharing_strategy === 'registration_category') {
                            return [registrationCategory.id, this.separateTeamIds[registrationCategory.id] || (registrationCategory.team ? registrationCategory.team.id : null)];
                        }
                    }

                    // For other categories, use existing team if available
                    let teamId = registrationCategory.team
                        ? registrationCategory.team.id
                        : null;

                    return [registrationCategory.id, teamId];
                }));
        },
        totalPrice() {
            return Object.entries(this.tickets)
                .map(([key, val]) => this.registrationCategories.find(i => i.id == key).current_price.amount * val)
                .reduce((acc, i) => acc + i, 0)
        },
        totalNumOfTickets() {
            return Object.entries(this.tickets).reduce((acc, [key, val]) => acc + val, 0);
        },
        enabled() {
            // Enable form fields if user has an ID (is authenticated)
            return Boolean(this.user.id);
        },
        userDetailsValid() {
            // Check if user details are complete and valid for allowing ticket selection
            const hasRequiredFields = Boolean(this.user.first_name) && Boolean(this.user.last_name);

            // Check required occasion additions are filled
            const requiredAdditions = this.occasion.occasion_additions.filter(oa => oa.validation && oa.validation.required);
            const hasRequiredAdditions = requiredAdditions.every(oa => {
                const value = this.additionalTransactionValues[oa.addition_id];
                return value !== undefined && value !== null && value !== '';
            });

            // Check for validation errors in user scope
            // Note: Validation is triggered by field blur/input events, not here
            const hasNoUserErrors = this.vErrors.items.filter(error => error.scope === 'user').length === 0;

            return hasRequiredFields && hasRequiredAdditions && hasNoUserErrors;
        },
        charities() {
            //Get a list of charity ID's
            let selectedRcIds = Object.keys(this.tickets).filter(key => this.tickets[key] > 0).map(x => +x);

            //Get a list of charity_id's to show
            let charityIdsToShow = this.occasion.registration_categories.filter(rc => selectedRcIds.includes(rc.id)).map(rc => rc.charity_ids_to_show).flat(2);

            //Get any charity ID's where a donation has been started
            // let inUseCharityIds = this.donations.filter(donation => donation.charity_id).map(donation => donation.charity_id);
            //
            // charityIdsToShow = charityIdsToShow.concat(inUseCharityIds);

            return this.occasion.charities.filter(charity => charityIdsToShow.includes(charity.id));
        },
        stepTickets() {
            // Step 1 is always user details (not shown in this component)
            return 2;
        },
        stepTeam() {
            // Step number for team section (comes after tickets)
            return 3;
        },
        stepDonations() {
            // Step number for donations (comes after team if team exists, otherwise after tickets)
            let step = this.stepTickets + 1; // Start after tickets

            // If team section is visible, add 1
            if (this.selectedGroupCategoriesWithSharedTeams.length > 0 || this.selectedGroupCategoriesWithSeparateTeams.length > 0) {
                step++;
            }

            return step;
        }
    },
    methods: {
        ParseInt,
        submit() {
            if (!this.validate()) {
                this.showValidationErrors = true;
                return false;
            } else {
                this.showValidationErrors = false;
            }

            this.$validator.validateAll('user').then((result) => {
                this.submitting = true;

                if (result) {
                    let additional_transaction_values = Object.entries(this.additionalTransactionValues)
                        .map(([addition_id, value]) => Object.assign({}, {
                            addition_id,
                            value,
                            'transaction_id': this.transaction.id
                        }));

                    this.transaction.user_id = this.user.id;
                    let transaction = this.transaction;

                    axios
                        .post('/api/v0/participants/bulk', {
                            additional_transaction_values,
                            donations: this.donations,
                            occasion_id: this.occasion.id,
                            teams: this.teams,
                            tickets: this.tickets,
                            transaction,
                            user: this.user,
                        })
                        .then((response) => {
                            let status = response.data.status;
                            if (status == "error") {

                                let errors = response.data.data.errors;

                                //Inject errors to validator
                                this.handleSubmitErrors(errors);

                                this.submitting = false;
                            } else if (status == "success") {
                                //Redirect to basket
                                window.location = response.data.data.redirect
                            }
                        }).catch((response) => {
                        this.submitting = false;
                        alert('There was a problem saving your tickets.  Please contact support@eventrac.co.uk');
                    })
                } else {
                    this.submitting = false;
                }

            }).catch(response => {
                alert('hee')
            });
        }
    },
}
</script>

<style scoped>
/* Select Tickets Heading */
.select-tickets-heading {
    color: #495057;
    font-weight: 600;
    font-size: 1.125rem;
    padding: 0.5rem 0;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid #dee2e6;
}

/* Tickets Container */
.tickets-container {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 1.5rem;
    background-color: #fff;
}

/* Team Container */
.team-container {
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 1.5rem;
    background-color: #fff;
}

@media (max-width: 576px) {
    .team-container {
        padding: 1rem;
    }

    .team-container >>> .btn {
        font-size: 0.875rem;
        padding: 0.5rem 0.75rem;
        white-space: normal;
        word-wrap: break-word;
    }

    .team-container >>> .btn-group {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .team-container >>> .btn-group .btn {
        flex: 1 1 calc(50% - 0.25rem);
        min-width: 0;
    }
}

/* Mobile View Styling */
.mobile-tickets-container {
    padding: 0;
}

.mobile-group-title {
    color: #2c3e50;
    font-weight: 600;
    font-size: 1rem;
    padding: 0.5rem 0;
    margin-bottom: 1rem;
    border-bottom: 1px solid #dee2e6;
}

.mobile-card-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.mobile-ticket-card {
    background-color: #fff;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 1rem;
}

.mobile-ticket-card-selected {
    border-color: #495057;
    background-color: #f8f9fa;
}

.mobile-card-content {
    margin-bottom: 1rem;
}

.mobile-card-title {
    font-size: 1rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 0.5rem;
}

.mobile-card-date {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
}

.mobile-card-price {
    font-size: 1rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
}

.mobile-card-quantity {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 1rem;
    border-top: 1px solid #e8e8e8;
}

.mobile-quantity-label {
    font-size: 0.875rem;
    color: #6c757d;
    font-weight: 500;
}

/* Totals Summary Panel */
.totals-summary-panel {
    background-color: transparent;
    border: none;
    border-radius: 0;
    padding: 1rem 0;
    text-align: right;
}

.totals-summary-panel-mobile {
    background-color: transparent;
    border: none;
    border-radius: 0;
    padding: 1rem 0;
    text-align: right;
}

.summary-item {
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    padding: 0;
    gap: 1rem;
}

.summary-label {
    font-size: 0.875rem;
    color: #6c757d;
    font-weight: 500;
}

.summary-value {
    font-size: 1rem;
    font-weight: 600;
    color: #495057;
    border-left: 1px solid #e8e8e8;
    padding-left: 1rem;
}

.shared-team-races {
    background-color: #f8f9fa;
    border-left: 3px solid #007bff;
    padding: 1rem;
    border-radius: 4px;
    max-width: 600px;
    margin: 0 auto;
}

.shared-team-races p {
    margin-bottom: 0.5rem;
    color: #495057;
}

.shared-team-races ul {
    list-style-type: disc;
    margin: 0;
    padding-left: 1.5rem;
}

.shared-team-races li {
    color: #212529;
    padding: 0.25rem 0;
}

.tickets-section-disabled {
    position: relative;
    opacity: 0.6;
    pointer-events: none;
}

.tickets-section-disabled::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.3);
    pointer-events: none;
    z-index: 1;
}

.tickets-section-disabled .alert {
    position: relative;
    z-index: 2;
    opacity: 1;
    pointer-events: auto;
}

.step-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background-color: #007bff;
    color: white;
    border-radius: 50%;
    font-size: 0.875rem;
    font-weight: 600;
}

.tickets-section-disabled .step-badge {
    background-color: #6c757d;
}

</style>



// WEBPACK FOOTER //
// src/Assets/components/Participants/Form/Bulk.vue